import { Injectable, OnDestroy, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ConfigService, setGlobalConfig } from './config.service.js';
import { DynamicPointCloudService } from './dynamic-pointcloud.service.js';
import { type PointCloudViewer, createViewer } from './pointcloud-runtime.js';
import { TerrainService } from './terrain.service.js';
import { ViewerCoreService } from './viewer-core.service.js';
import { AuthService } from '../auth/auth.service.js';
import {
  type DeepLinkQueryParams,
  computeDeepLinkCameraView,
  parseDeepLinkQueryParams,
} from '../viewer-utils/deep-link-view.js';

@Injectable({ providedIn: 'root' })
export class ViewerService implements OnDestroy {
  private config = inject(ConfigService);
  private authService = inject(AuthService);
  private core = inject(ViewerCoreService);
  private terrain = inject(TerrainService);
  private dynamicPointCloud = inject(DynamicPointCloudService);

  // ─── Initialization ─────────────────────────────────────────────────────────

  parseParams(): {
    tenant: string | null;
    modelId: string | null;
    deepLinkView: DeepLinkQueryParams | null;
  } {
    const urlParams = new URLSearchParams(window.location.search);
    const tenant = urlParams.get('tenant');
    const modelId = urlParams.get('modelId');
    const deepLinkView = parseDeepLinkQueryParams(urlParams);
    return { tenant, modelId, deepLinkView };
  }

  async initViewer(container: HTMLElement): Promise<PointCloudViewer | null> {
    const { modelId, tenant, deepLinkView } = this.parseParams();

    const viewer = createViewer(container);
    this.core.setViewer(viewer);
    viewer.setEDLEnabled(true);
    viewer.setFOV(60);
    viewer.setPointBudget(this.config.pointBudget());
    viewer.setBackground('skybox');

    const configObject = {
      pointBudget: this.config.pointBudget(),
      maxNodesLoading: this.config.maxNodesLoading(),
      numNodesLoading: this.config.numNodesLoading(),
      transformVersions: new Map(),
      framenumber: this.config.framenumber(),
    };
    setGlobalConfig(configObject);

    if (typeof viewer.setVisibilityConfig === 'function') {
      viewer.setVisibilityConfig(configObject);
    }

    const accessToken = await firstValueFrom(this.authService.accessToken$);
    this.config.updateAccessToken(accessToken);

    if (deepLinkView) {
      // Deep link from the OpenLayers 2D map: jump straight to the requested
      // WGS84 location/zoom, facing the camera vertically down.
      const cameraView = computeDeepLinkCameraView(deepLinkView, {
        viewportHeightPx: container.clientHeight || window.innerHeight,
        verticalFovDegrees: viewer.getFOV(),
      });
      viewer.scene.view.yaw = cameraView.yaw;
      viewer.scene.view.pitch = cameraView.pitch;
      viewer.scene.view.radius = cameraView.radius;
      viewer.scene.view.position.copy(cameraView.position);
    } else if (!modelId) {
      const swissCenterX = 2660000;
      const swissCenterY = 1190000;
      const groundLevel = 500;
      const viewRadius = 150000;

      viewer.scene.view.yaw = 0;
      viewer.scene.view.pitch = -Math.PI / 2;
      viewer.scene.view.radius = viewRadius;
      viewer.scene.view.position.set(swissCenterX, swissCenterY, groundLevel + viewRadius);
    }

    if (!modelId) {
      // Only load the overview layers (terrain) when no specific
      // model is requested. With a modelId we load just that model directly.
      await this.terrain.loadQuantizedMeshTerrain('aerial');
      await this.terrain.loadDigitalTwinModels();
    }

    // Always start dynamic point cloud service (loads initial modelId if provided)
    this.dynamicPointCloud.start(tenant, modelId);

    return viewer;
  }

  ngOnDestroy(): void {
    this.disposeViewer();
  }

  disposeViewer(): void {
    const viewer = this.core.viewer();

    // Stop dynamic point cloud monitoring
    this.dynamicPointCloud.stop();

    if (viewer) {
      if (viewer.tilesManager) {
        try {
          viewer.tilesManager.dispose();
        } catch {
          /* ignore */
        }
      }
      if (typeof viewer.dispose === 'function') {
        try {
          viewer.dispose();
        } catch {
          /* ignore */
        }
      }
    }
    this.core.clearViewer();
    this.config.updateSasToken(null);
    this.config.updateAccessToken(null);
  }
}
